import { Compiler } from '@/dsl/compilers';
import type { UnifiedContext } from '../context';
import { isPresent } from './utilty';

const OPTION_DISCOUNT_TOKENS = ['value', 'percentage', 'value-monthly', 'percentage-monthly'] as const;
type OptionDiscountToken = typeof OPTION_DISCOUNT_TOKENS[number];
type OptionCtx = NonNullable<UnifiedContext['option']>;

const STATE_DISCOUNT_TOKENS = [
  'min-value',
  'max-value',
  'min-percentage',
  'max-percentage',
  'min-value-monthly',
  'max-value-monthly',
  'min-percentage-monthly',
  'max-percentage-monthly'
] as const;
type StateDiscountToken = typeof STATE_DISCOUNT_TOKENS[number];
type StateCtx = NonNullable<UnifiedContext['state']>;

const OPTION_DISCOUNT_AVAILABLE = {
  'value'             : (option: OptionCtx) => isPresent(option.getDiscount({ symbol: false, percentage: false })),
  'percentage'        : (option: OptionCtx) => isPresent(option.getDiscount({ symbol: false, percentage: true })),
  'value-monthly'     : (option: OptionCtx) => isPresent(option.getDiscount({ symbol: false, percentage: false, monthly: true })),
  'percentage-monthly': (option: OptionCtx) => isPresent(option.getDiscount({ symbol: false, percentage: true, monthly: true }))
} satisfies Record<OptionDiscountToken, (option: OptionCtx) => boolean>;

const OPTION_DISCOUNT_RENDER = {
  'value'             : (option: OptionCtx) => option.getDiscount({ percentage: false }),
  'percentage'        : (option: OptionCtx) => option.getDiscount({ percentage: true }),
  'value-monthly'     : (option: OptionCtx) => option.getDiscount({ percentage: false, monthly: true }),
  'percentage-monthly': (option: OptionCtx) => option.getDiscount({ percentage: true, monthly: true })
} satisfies Record<OptionDiscountToken, (option: OptionCtx) => string>;

const STATE_DISCOUNT_AVAILABLE = {
  'min-value'             : (state: StateCtx) => Boolean(state.discount.min.fmt),
  'max-value'             : (state: StateCtx) => Boolean(state.discount.max.fmt),
  'min-value-monthly'     : (state: StateCtx) => Boolean(state.discount.monthly.min.fmt),
  'max-value-monthly'     : (state: StateCtx) => Boolean(state.discount.monthly.max.fmt),
  'min-percentage'        : (state: StateCtx) => Boolean(state.discount.percentage.min.fmt),
  'max-percentage'        : (state: StateCtx) => Boolean(state.discount.percentage.max.fmt),
  'min-percentage-monthly': (state: StateCtx) => Boolean(state.discount.percentage.monthly.min.fmt),
  'max-percentage-monthly': (state: StateCtx) => Boolean(state.discount.percentage.monthly.max.fmt)
} satisfies Record<StateDiscountToken, (state: StateCtx) => boolean>;

const STATE_DISCOUNT_RENDER = {
  'min-value'             : (state: StateCtx) => state.discount.min.fmt,
  'max-value'             : (state: StateCtx) => state.discount.max.fmt,
  'min-value-monthly'     : (state: StateCtx) => state.discount.monthly.min.fmt,
  'max-value-monthly'     : (state: StateCtx) => state.discount.monthly.max.fmt,
  'min-percentage'        : (state: StateCtx) => state.discount.percentage.min.fmt,
  'max-percentage'        : (state: StateCtx) => state.discount.percentage.max.fmt,
  'min-percentage-monthly': (state: StateCtx) => state.discount.percentage.monthly.min.fmt,
  'max-percentage-monthly': (state: StateCtx) => state.discount.percentage.monthly.max.fmt
} satisfies Record<StateDiscountToken, (state: StateCtx) => string>;

// Handles both data-store-discount (option-level) and data-store-context-discount (state-level)
export const handleDiscount = (el: HTMLElement, ctx: UnifiedContext) => {
  const { storeDiscount, storeContextDiscount } = el.dataset;
  const option = ctx.option;

  // Option-level discount
  if (storeDiscount && option) {
    const chosen = Compiler.enum<OptionDiscountToken>({
      expr       : storeDiscount,
      allowed    : OPTION_DISCOUNT_TOKENS,
      isAvailable: (token) => OPTION_DISCOUNT_AVAILABLE[token](option)
    });

    if (chosen) {
      el.textContent = OPTION_DISCOUNT_RENDER[chosen](option);
      return;
    }
  }

  const state = ctx.state;

  // State-level discount
  if (storeContextDiscount && state) {
    const chosen = Compiler.enum<StateDiscountToken>({
      expr       : storeContextDiscount,
      allowed    : STATE_DISCOUNT_TOKENS,
      isAvailable: (token) => STATE_DISCOUNT_AVAILABLE[token](state)
    });

    if (chosen) {
      el.textContent = STATE_DISCOUNT_RENDER[chosen](state);
    }
  }
};
